import { EonUiIcon, EonUiTooltip } from "@eon-ui/eon-ui-components-react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { useEffect, useMemo } from "react";
import AppOnboardingPreviewCard from "../AppOnboardingPreviewCard";
import { ModuleType } from "../OnboardingPortal.types";
import OnboardingPortalLinkArrow from "../components/OnboardingPortalLinkArrow";
import OnboardingPortalQuickActions from "../components/OnboardingPortalQuickActions";
import OnboardingPortalAppIcon from '../components/OnboardingPortalAppIcon';
import OnboardingPortalTableHeaders from '../components/OnboardingPortalTableHeaders';
import { useLanguage } from "@me8eon/language";

const HomeScreenEmptyStateScreen = ({
    setShowModule,
    isAppsEmpty,
    isQnAEmpty,
}: {
    setShowModule: (module: ModuleType) => void;
    isAppsEmpty: boolean;
    isQnAEmpty: boolean;
}) => {
    const [language] = useLanguage()
    const t = useTranslation();
    const title = useMemo(() => {
        if (isAppsEmpty && isQnAEmpty)
            return t("onboardingPortal.noData.dashboardEmpty");
        if (isAppsEmpty) return t("onboardingPortal.noData.dashboardAppsEmpty");
        if (isQnAEmpty) return t("onboardingPortal.noData.dashboardQnAEmpty");
        return "";
    }, [isAppsEmpty, isQnAEmpty, language]);
    const showAppsBtn = useMemo(() => {
        return isAppsEmpty;
    }, [isAppsEmpty, language]);
    const showQnABtn = useMemo(() => {
        return isQnAEmpty;
    }, [isQnAEmpty, language]);
    return (
        <>
            <div
                className={
                    isAppsEmpty && isQnAEmpty
                        ? "onboarding-portal--home-screen--empty-state"
                        : "onboarding-portal--home-screen--empty-state--card"
                }
            >
                <div className="onboarding-portal--home-screen--empty-state--text">
                    <EonUiIcon name="info" scheme="red500" />
                    <span>{title}</span>
                </div>
                <div className="onboarding-portal--home-screen--empty-state--btn-group">
                    {showAppsBtn && (
                        <div
                            className="onboarding-portal--home-screen--empty-state--btn"
                            onClick={() => setShowModule("onboard-apps")}
                        >
                            <EonUiIcon
                                name="dashboard_02"
                                scheme="red500"
                                size="small"
                            />
                            <span>
                                {t(
                                    "onboardingPortal.quickActions.onboardNewApp"
                                )}
                            </span>
                        </div>
                    )}
                    {showQnABtn && (
                        <div
                            className="onboarding-portal--home-screen--empty-state--btn"
                            onClick={() => setShowModule("onboard-qna")}
                        >
                            <EonUiIcon
                                name="question_mark"
                                scheme="red500"
                                size="small"
                            />
                            <span>
                                {t(
                                    "onboardingPortal.quickActions.onboardNewQnA"
                                )}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const OnboardingDashboard = () => {
    const t = useTranslation();
    const {
        setShowModule,
        fetchOnboardedApps,
        fetchOnboardedQnA,
        onboardedApps,
        onboardedQnA,
        onEditApp,
        onEditQnA,
        onViewApp,
        onViewQnA,
    } = useOnboardingPortalStore();
    const jwt = useJwtToken();

    useEffect(() => {
        if (fetchOnboardedApps) fetchOnboardedApps(jwt);
        if (fetchOnboardedQnA) fetchOnboardedQnA(jwt);
    }, [fetchOnboardedApps, fetchOnboardedQnA, jwt]);
    return (
        <>
            {onboardedApps.length === 0 && onboardedQnA.length === 0 ? (
                <HomeScreenEmptyStateScreen
                    setShowModule={setShowModule}
                    isAppsEmpty
                    isQnAEmpty
                />
            ) : (
                <div className="onboarding-portal--home-screen">
                    <div className="onboarding-portal--home-screen--content">
                        <div className="onboarding-portal--home-screen--content--stats">
                            <div className="onboarding-portal--home-screen--content--stats--card">
                                <div className="onboarding-portal--home-screen--content--stats--card--header">
                                    <EonUiIcon name="modular" scheme="red500" />
                                    <div className="onboarding-portal--home-screen--content--stats--card--header--text">
                                        {t(
                                            "onboardingPortal.dashboard.appsTitle"
                                        )}
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("manage-apps")
                                        }
                                    />
                                </div>
                                <div className="onboarding-portal--home-screen--content--stats--card--title">
                                    {onboardedApps.length}
                                </div>
                                <div className="onboarding-portal--home-screen--content--stats--card--footer">
                                    {t("onboardingPortal.dashboard.appsText")}
                                </div>
                            </div>
                            <div className="onboarding-portal--home-screen--content--stats--card">
                                <div className="onboarding-portal--home-screen--content--stats--card--header">
                                    <EonUiIcon name="modular" scheme="red500" />
                                    <div className="onboarding-portal--home-screen--content--stats--card--header--text">
                                        {t(
                                            "onboardingPortal.dashboard.qnaTitle"
                                        )}
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("manage-qna")
                                        }
                                    />
                                </div>
                                <div className="onboarding-portal--home-screen--content--stats--card--title">
                                    {onboardedQnA.length}
                                </div>
                                <div className="onboarding-portal--home-screen--content--stats--card--footer">
                                    {t("onboardingPortal.dashboard.qnaText")}
                                </div>
                            </div>
                        </div>
                        {onboardedApps.length > 0 && (
                            <div className="onboarding-portal--content--card">
                                <div className="onboarding-portal--content--card--header">
                                    <div className="onboarding-portal--content--card--header--text">
                                        {t(
                                            "onboardingPortal.tableHeaders.apps"
                                        )}{" "}
                                        ({onboardedApps.length})
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("manage-apps")
                                        }
                                    />
                                </div>
                                <table className="onboarding-portal--table">
                                    <OnboardingPortalTableHeaders
                                        headers={[
                                            "onboardingPortal.tableHeaders.icon",
                                            "onboardingPortal.tableHeaders.title",
                                            "onboardingPortal.tableHeaders.subTitle",
                                            "onboardingPortal.tableHeaders.status",
                                            "onboardingPortal.tableHeaders.action",
                                        ]}
                                        t={t}
                                    />
                                    <tbody>
                                        {onboardedApps
                                            .slice(0, 3)
                                            .map((app) => (
                                                <tr key={app.id}>
                                                    <td>
                                                        <OnboardingPortalAppIcon
                                                            icon={app.icon}
                                                            iconUrl={
                                                                app.iconDataUrl
                                                            }
                                                        />
                                                    </td>
                                                    <td>{app.title}</td>
                                                    <td>{app.subTitle}</td>
                                                    <td>
                                                        <span
                                                            className={`status--${app.status}`}
                                                        >
                                                            {t(
                                                                `onboardingPortal.statusLabel.${app.status}`
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="actions-row">
                                                            <div>
                                                                <span className="action--preview">
                                                                    Preview
                                                                </span>
                                                                <EonUiTooltip
                                                                    visibility="onHover"
                                                                    placement="top"
                                                                >
                                                                    <div className="onboarding-portal--form--preview">
                                                                        <AppOnboardingPreviewCard
                                                                            data={{
                                                                                title: app.title,
                                                                                subTitle:
                                                                                    app.subTitle,
                                                                                iconUrl:
                                                                                    app.iconDataUrl,
                                                                                icon: app.icon,
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </EonUiTooltip>
                                                            </div>
                                                            <span
                                                                className="action--view"
                                                                onClick={() =>
                                                                    onViewApp(
                                                                        app
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "onboardingPortal.viewBtn"
                                                                )}
                                                            </span>
                                                            <span
                                                                className="action--edit"
                                                                onClick={() =>
                                                                    onEditApp(
                                                                        app
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "onboardingPortal.editBtn"
                                                                )}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {onboardedQnA.length > 0 && (
                            <div className="onboarding-portal--content--card">
                                <div className="onboarding-portal--content--card--header">
                                    <div className="onboarding-portal--content--card--header--text">
                                        {t("onboardingPortal.tableHeaders.qna")}{" "}
                                        ({onboardedQnA.length})
                                    </div>
                                    <OnboardingPortalLinkArrow
                                        onClick={() =>
                                            setShowModule("manage-qna")
                                        }
                                    />
                                </div>
                                <table className="onboarding-portal--table">
                                    <OnboardingPortalTableHeaders
                                        headers={[
                                            "onboardingPortal.tableHeaders.question",
                                            "onboardingPortal.tableHeaders.answer",
                                            "onboardingPortal.tableHeaders.category",
                                            "onboardingPortal.tableHeaders.status",
                                            "onboardingPortal.tableHeaders.action",
                                        ]}
                                        t={t}
                                    />
                                    <tbody>
                                        {onboardedQnA.slice(0, 3).map((qna) => (
                                            <tr key={qna.id}>
                                                <td>{qna.question}</td>
                                                <td>{qna.answer}</td>
                                                <td>{qna.category}</td>
                                                <td
                                                    className={`status--${qna.status}`}
                                                >
                                                    <span
                                                        className={`status--${qna.status}`}
                                                    >
                                                        {t(
                                                            `onboardingPortal.statusLabel.${qna.status}`
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="actions-row">
                                                        <span
                                                            className="action--view"
                                                            onClick={() =>
                                                                onViewQnA(qna)
                                                            }
                                                        >
                                                            {t(
                                                                "onboardingPortal.viewBtn"
                                                            )}
                                                        </span>
                                                        <span
                                                            className="action--edit"
                                                            onClick={() =>
                                                                onEditQnA(qna)
                                                            }
                                                        >
                                                            {t(
                                                                "onboardingPortal.editBtn"
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {onboardedApps.length === 0 && (
                            <HomeScreenEmptyStateScreen
                                setShowModule={setShowModule}
                                isAppsEmpty
                                isQnAEmpty={false}
                            />
                        )}
                        {onboardedQnA.length === 0 && (
                            <HomeScreenEmptyStateScreen
                                setShowModule={setShowModule}
                                isAppsEmpty={false}
                                isQnAEmpty
                            />
                        )}
                    </div>
                    <div className="onboarding-portal--side-bar">
                        <OnboardingPortalQuickActions />
                    </div>
                </div>
            )}
        </>
    );
};

export default OnboardingDashboard;
